/**
 * Вспомогательные функции для компонент‑аватарок.
 *
 * Файл содержит две утилиты:
 * 1. `getInitials` — возвращает инициалы (до двух букв) из полного имени.
 * 2. `stringToColor` — создаёт детерминированный HSL‑цвет на основе строки.
 *    Один и тот же вход всегда даст одинаковый цвет, поэтому функцию удобно
 *    использовать для фонов аватарок.
 *
 * @example
 * getInitials("Константин Константиновский") // "КК"
 * stringToColor("Константин Константиновский") // "hsl(123, 70%, 40%)"
 */

/**
 * Извлекает инициалы (до двух символов) из переданной строки‑имени.
 *
 * @param name Полное имя пользователя, слова разделены пробелами.
 * @returns Строка из одной или двух заглавных букв. Если имя пустое — возвращается
 *          пустая строка.
 */
export const getInitials = (name: string): string =>
	name
		.trim()
		.split(/\s+/) // делим по любым пробелам, схлопывая подряд идущие
		.filter(Boolean) // убираем пустые подстроки
		.slice(0, 2) // берём максимум два слова
		.map((word) => word[0].toUpperCase())
		.join('');

/**
 * Генерирует HSL‑цвет из произвольной строки.
 *
 * Алгоритм создаёт простой хэш, переводит его в диапазон 0…359 и
 * подставляет как оттенок (Hue). Насыщенность и светлота фиксированы —
 * 70 % и 40 % соответственно.
 *
 * @param str Любая строка (например, имя или e‑mail).
 * @returns Цвет в формате `hsl(<hue>, 70%, 40%)`.
 */
export const stringToColor = (str: string): string => {
	let hash = 0;

	// Простейшая djb2‑подобная функция хэширования
	for (const char of str) {
		const codePoint = char.codePointAt(0) ?? 0;
		hash = (Math.imul(hash, 31) + codePoint) % 2_147_483_647;
	}

	const hue = Math.abs(hash) % 360; // приводим к диапазону 0–359

	return `hsl(${hue}, 70%, 40%)`;
};
